<script setup>
import { watch, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BookingCard from './BookingCard.vue';
import BookingFormModal from './BookingFormModal.vue';
import ViewBookingModal from './ViewBookingModal.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('contactBookings/getUIFlags');
const bookingsByContact = useMapGetter(
  'contactBookings/getAllBookingsByContactId'
);
const isFetching = computed(() => uiFlags.value.isFetching);
const contactId = computed(() => props.contactId);
const shouldShowCreateModal = ref(false);
const shouldShowViewModal = ref(false);
const selectedBooking = ref(null);
const editingBooking = ref(null);

const bookings = computed(() => {
  if (!contactId.value) return [];
  return bookingsByContact.value(contactId.value) || [];
});

const openCreateModal = () => {
  if (!contactId.value) return;
  shouldShowCreateModal.value = true;
};

const closeCreateModal = () => {
  shouldShowCreateModal.value = false;
  editingBooking.value = null;
};

const onViewBooking = booking => {
  selectedBooking.value = booking;
  shouldShowViewModal.value = true;
};

const closeViewModal = () => {
  shouldShowViewModal.value = false;
  selectedBooking.value = null;
};

const onEditBooking = booking => {
  editingBooking.value = booking;
  shouldShowCreateModal.value = true;
};

const onDelete = async bookingId => {
  if (!contactId.value || !bookingId) return;

  await store.dispatch('contactBookings/delete', {
    bookingId,
    contactId: contactId.value,
  });
};

watch(
  contactId,
  newContactId => {
    closeCreateModal();
    closeViewModal();
    if (newContactId) {
      store.dispatch('contactBookings/get', { contactId: newContactId });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div class="px-4 pt-3 pb-2">
      <NextButton
        ghost
        xs
        icon="i-lucide-plus"
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ADD_BOOKING')"
        :disabled="!contactId || isFetching"
        @click="openCreateModal"
      />
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="bookings.length"
      class="flex flex-col max-h-[400px] overflow-y-auto"
    >
      <BookingCard
        v-for="booking in bookings"
        :key="booking.id"
        :booking="booking"
        @view="onViewBooking"
      />
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.EMPTY_STATE') }}
    </p>

    <BookingFormModal
      v-model:show="shouldShowCreateModal"
      :contact-id="contactId"
      :booking="editingBooking"
      @close="closeCreateModal"
    />

    <ViewBookingModal
      v-model:show="shouldShowViewModal"
      :booking="selectedBooking"
      @close="closeViewModal"
      @delete="onDelete"
      @edit="onEditBooking"
    />
  </div>
</template>
